import { CHIP_SIZE, CHIP_VARIANT } from 'enums';

export type ChipVariant = `${CHIP_VARIANT}`;
export type ChipSize = `${CHIP_SIZE}`;

interface ChipProps {
  text: string;
  variant?: ChipVariant;
  size?: ChipSize;
  className?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
}

const Chip = ({
  text,
  variant = CHIP_VARIANT.FILLED,
  size = CHIP_SIZE.MD,
  className = '',
  icon,
  onClick
}: ChipProps) => {
  const sizeClasses = {
    xs: '~px-2/2.5 ~py-0/0.5 ~gap-0.5/1 ~text-[0.5rem]/[0.6rem]',
    sm: '~px-2.5/3 ~py-0.5/1 ~gap-1/1.5 ~text-[0.625rem]/xs',
    md: '~px-3/4 ~py-1/1.5 ~gap-1.5/2 ~text-xs/sm',
    lg: '~px-4/5 ~py-1.5/2 ~gap-2/2.5 ~text-sm/base'
  } as Record<ChipSize, string>;

  const variantClasses: Record<ChipVariant, string> = {
    filled:
      'rounded-full bg-gray-200 dark:bg-secondary-dark-400 text-gray-900 dark:text-secondary-dark-900 dark:shadow-sm',
    outlined:
      'rounded-full border border-gray-300 dark:border-secondary-dark-500 bg-transparent text-gray-900 dark:text-secondary-dark-900',
    ghost:
      'rounded-full bg-transparent text-gray-700 dark:text-secondary-dark-800',
    warning:
      'rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100',
    caution:
      'rounded-full bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-100',
    success:
      'rounded-full bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-100',
    info: 'rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-100',
    danger:
      'rounded-full bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-100',
    ai:
      'rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-100'
  };

  const classes = `${sizeClasses[size]} ${variantClasses[variant]} w-fit flex items-center shadow-sm lg:shadow ${className}`;

  const Component = onClick ? 'button' : 'div';

  return (
    <Component
      onClick={onClick}
      type={onClick ? 'button' : undefined}
      className={classes}>
      {icon ? <span className='mr-1'>{icon}</span> : null}
      <span>{text}</span>
    </Component>
  );
};

export const ChipSmall = (props: Omit<ChipProps, 'size'>) => (
  <Chip {...props} size='sm' />
);
export const ChipMedium = (props: Omit<ChipProps, 'size'>) => (
  <Chip {...props} size='md' />
);
export const ChipLarge = (props: Omit<ChipProps, 'size'>) => (
  <Chip {...props} size='lg' />
);
export const ChipXSmall = (props: Omit<ChipProps, 'size'>) => (
  <Chip {...props} size='xs' />
);

export default Chip;
